import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  fetchHomeowners,
  selectHomeownersHasListed,
  selectHomeownersHasMore,
  selectHomeownersList,
  selectHomeownersListError,
  selectHomeownersListLoading,
  selectHomeownersNextToken,
  selectHomeownersTotalListed,
} from '@/features/homeowners';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

const PAGE_LIMIT = 50;

function normalize(value: string) {
  return value.trim().toLowerCase();
}

function getDisplayName(firstName?: string, lastName?: string, email?: string) {
  const fullName = `${firstName?.trim() ?? ''} ${lastName?.trim() ?? ''}`.trim();
  return fullName || email || 'Unknown user';
}

function formatDate(value?: number) {
  if (!value) return 'N/A';

  const timestamp = value < 1_000_000_000_000 ? value * 1000 : value;
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getVerificationBadge(isVerified?: boolean) {
  return isVerified ? <Badge variant="default">Verified</Badge> : <Badge variant="outline">Unverified</Badge>;
}

export default function HomeownersPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const homeowners = useAppSelector(selectHomeownersList);
  const isLoading = useAppSelector(selectHomeownersListLoading);
  const error = useAppSelector(selectHomeownersListError);
  const hasListed = useAppSelector(selectHomeownersHasListed);
  const hasMore = useAppSelector(selectHomeownersHasMore);
  const nextToken = useAppSelector(selectHomeownersNextToken);
  const totalListed = useAppSelector(selectHomeownersTotalListed);

  const [query, setQuery] = useState('');

  useEffect(() => {
    if (hasListed) return;

    dispatch(fetchHomeowners({ limit: PAGE_LIMIT }))
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load home owners');
      });
  }, [dispatch, hasListed]);

  const filteredHomeowners = useMemo(() => {
    const q = normalize(query);
    if (!q) return homeowners;

    return homeowners.filter((homeowner) => {
      const haystack = [
        homeowner.applicationUserId,
        homeowner.email,
        homeowner.phone,
        homeowner.firstName,
        homeowner.lastName,
        homeowner.role,
        homeowner.status,
        homeowner.typeName,
      ]
        .filter(Boolean)
        .join(' ');

      return normalize(haystack).includes(q);
    });
  }, [homeowners, query]);

  const handleRefresh = async () => {
    if (isLoading) return;

    try {
      await dispatch(fetchHomeowners({ limit: PAGE_LIMIT })).unwrap();
      toast.success('Home owners refreshed');
    } catch (err) {
      const errorPayload = err as { message?: string };
      toast.error(errorPayload?.message || 'Failed to refresh home owners');
    }
  };

  const loadMore = async () => {
    if (isLoading || !hasMore) return;

    try {
      await dispatch(fetchHomeowners({ limit: PAGE_LIMIT, nextToken: nextToken ?? undefined })).unwrap();
    } catch (err) {
      const errorPayload = err as { message?: string };
      toast.error(errorPayload?.message || 'Failed to load more home owners');
    }
  };

  if (isLoading && homeowners.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size={80} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Admin · Home Owners</h1>
          <p className="text-muted-foreground text-sm">Browse homeowner users in the system.</p>
          <ErrorNotice className="mt-2" message={error} title="Failed to Load Home Owners" />
        </div>

        <Button type="button" variant="outline" onClick={handleRefresh} disabled={isLoading}>
          {isLoading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserRound className="h-5 w-5" />
            Home Owners ({totalListed})
          </CardTitle>
          <CardDescription>Click a home owner row to open details.</CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by email, phone, name, id, role, or status..."
          />

          {filteredHomeowners.length === 0 ? (
            <div className="border-muted-foreground/25 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center">
              <UserRound className="text-muted-foreground mb-2 h-8 w-8" />
              <h3 className="font-medium">No home owners found</h3>
              <p className="text-muted-foreground text-sm">
                {query ? 'Try a different search term.' : 'There are no home owners in the system yet.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email Verified</TableHead>
                  <TableHead>Phone Verified</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHomeowners.map((homeowner) => (
                  <TableRow
                    key={homeowner.applicationUserId}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(`/admin/homeowners/${encodeURIComponent(homeowner.applicationUserId)}`, {
                        state: { fromHomeownersList: true },
                      })
                    }
                  >
                    <TableCell className="font-medium">
                      {getDisplayName(homeowner.firstName, homeowner.lastName, homeowner.email)}
                    </TableCell>
                    <TableCell>{homeowner.email || 'N/A'}</TableCell>
                    <TableCell>{homeowner.phone || 'N/A'}</TableCell>
                    <TableCell>
                      {homeowner.status ? <Badge variant="secondary">{homeowner.status}</Badge> : 'N/A'}
                    </TableCell>
                    <TableCell>{getVerificationBadge(homeowner.emailVerified)}</TableCell>
                    <TableCell>{getVerificationBadge(homeowner.phoneVerified)}</TableCell>
                    <TableCell>{formatDate(homeowner.updatedAt || homeowner.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={loadMore}
              disabled={isLoading || !hasMore || filteredHomeowners.length !== homeowners.length}
            >
              {filteredHomeowners.length !== homeowners.length
                ? 'Search active'
                : isLoading
                  ? 'Loading…'
                  : hasMore
                    ? 'Load more'
                    : 'No more home owners'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
